import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, User, Clock, Filter, Search, Lock } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const NotesPanel = ({ leads, userId, onNoteAdded }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteFormData, setNoteFormData] = useState({
    lead_id: '',
    content: '',
    note_type: 'general',
    is_private: false,
    created_by: userId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    if (leads?.length > 0) {
      setSelectedLead(leads?.[0]);
    }
  }, [leads]);

  useEffect(() => {
    if (selectedLead?.id) {
      loadNotes();
    }
  }, [selectedLead]);

  const loadNotes = async () => {
    if (!selectedLead?.id) return;
    
    setLoading(true);
    try {
      const { data, error: notesError } = await funnelService?.getLeadNotes(selectedLead?.id);
      if (notesError) {
        setError(notesError);
      } else {
        setNotes(data || []);
      }
    } catch (err) {
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = () => {
    setNoteFormData({
      lead_id: selectedLead?.id || '',
      content: '',
      note_type: 'general',
      is_private: false,
      created_by: userId
    });
    setShowNoteModal(true);
  };

  const handleNoteSubmit = async (e) => {
    e?.preventDefault();
    
    if (!noteFormData?.lead_id || !noteFormData?.content?.trim()) {
      setError('Please select a lead and enter note content');
      return;
    }

    try {
      const { data, error: submitError } = await funnelService?.createNote({
        ...noteFormData,
        content: noteFormData?.content?.trim()
      });

      if (submitError) {
        setError(submitError);
      } else {
        setShowNoteModal(false);
        setNoteFormData({
          lead_id: '',
          content: '',
          note_type: 'general',
          is_private: false,
          created_by: userId
        });
        await loadNotes();
        onNoteAdded?.();
      }
    } catch (err) {
      setError('Failed to create note');
    }
  };

  const getNoteTypeColor = (noteType) => {
    const colors = {
      general: 'bg-gray-100 text-gray-800 border-gray-200',
      contact: 'bg-blue-100 text-blue-800 border-blue-200',
      meeting: 'bg-green-100 text-green-800 border-green-200',
      follow_up: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      qualification: 'bg-purple-100 text-purple-800 border-purple-200',
      proposal: 'bg-orange-100 text-orange-800 border-orange-200',
      objection: 'bg-red-100 text-red-800 border-red-200',
      close: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };
    return colors?.[noteType] || colors?.general;
  };

  const noteTypeOptions = [
    { value: 'general', label: 'General' },
    { value: 'contact', label: 'Contact' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'qualification', label: 'Qualification' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'objection', label: 'Objection' },
    { value: 'close', label: 'Close' }
  ];

  const filteredNotes = notes?.filter(note => {
    const matchesSearch = note?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         note?.note_type?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesType = typeFilter === 'all' || note?.note_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const formatRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date?.toLocaleDateString();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Notes & Communication</h3>
          <p className="text-sm text-gray-600">Track interactions and important information for your leads</p>
        </div>
        <button
          onClick={handleCreateNote}
          disabled={!selectedLead}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            ×
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lead Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Select Lead</h4>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {leads?.map((lead) => {
                const leadNotes = notes?.filter(note => note?.lead_id === lead?.id);
                return (
                  <button
                    key={lead?.id}
                    onClick={() => setSelectedLead(lead)}
                    className={`
                      w-full text-left p-3 rounded-md border transition-colors
                      ${selectedLead?.id === lead?.id
                        ? 'bg-blue-50 border-blue-200 text-blue-900' :'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {lead?.communication_contacts?.name || 'Unknown Contact'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {lead?.communication_contacts?.company || 'No Company'}
                        </p>
                        <div className="flex items-center mt-1">
                          <MessageSquare className="h-3 w-3 mr-1 text-gray-400" />
                          <p className="text-xs text-gray-500">
                            {leadNotes?.length} notes
                          </p>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
              
              {leads?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No leads available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notes Display */}
        <div className="lg:col-span-3">
          <div className="bg-white shadow rounded-lg">
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h4 className="text-sm font-medium text-gray-900">
                  Notes
                  {selectedLead && (
                    <span className="ml-2 text-sm text-gray-500">
                      for {selectedLead?.communication_contacts?.name}
                    </span>
                  )}
                </h4>
                
                {/* Search and Filter */}
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10 pr-4 py-2 text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e?.target?.value)}
                      className="text-sm border-gray-300 rounded-md"
                    >
                      <option value="all">All Types</option>
                      {noteTypeOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4">
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              )}

              <div className="space-y-4">
                {filteredNotes?.map((note) => (
                  <div key={note?.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs rounded border ${getNoteTypeColor(note?.note_type)}`}>
                          {note?.note_type}
                        </span>
                        {note?.is_private && (
                          <div className="flex items-center text-xs text-gray-500">
                            <Lock className="h-3 w-3 mr-1" />
                            Private
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatRelativeTime(note?.created_at)}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{note?.content}</p>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {note?.user_profiles ? 
                          `${note?.user_profiles?.first_name} ${note?.user_profiles?.last_name}` : 
                          'Unknown User'
                        }
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span>{new Date(note.created_at)?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredNotes?.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      {selectedLead ? (
                        searchTerm || typeFilter !== 'all' ? 'No notes match your search criteria': 'No notes found for this lead'
                      ) : (
                        'Select a lead to view notes'
                      )}
                    </p>
                    {selectedLead && !searchTerm && typeFilter === 'all' && (
                      <button
                        onClick={handleCreateNote}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Add the first note
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-md shadow-lg">
            <form onSubmit={handleNoteSubmit}>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Add Note</h3>
                  <button
                    type="button"
                    onClick={() => setShowNoteModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                      {selectedLead?.communication_contacts?.name || 'No lead selected'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Note Type
                    </label>
                    <select
                      value={noteFormData?.note_type}
                      onChange={(e) => setNoteFormData(prev => ({ ...prev, note_type: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    >
                      {noteTypeOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Note Content
                    </label>
                    <textarea
                      rows={4}
                      value={noteFormData?.content}
                      onChange={(e) => setNoteFormData(prev => ({ ...prev, content: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your note here..."
                      required
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      id="is_private"
                      type="checkbox"
                      checked={noteFormData?.is_private}
                      onChange={(e) => setNoteFormData(prev => ({ ...prev, is_private: e?.target?.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_private" className="ml-2 block text-sm text-gray-900">
                      Make this note private
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowNoteModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPanel;